import { computed, ref } from '@vue/composition-api';

// ===== Start: Search normalization =====
// Converts all searchable fields to a comparable lowercase string.
const normalizeSearchValue = (value) => String(value ?? '').toLowerCase().trim();
// ===== End: Search normalization =====

export function useCrisisUnitRows({ props, getBranchKey, isStateExpanded, isMunicipalityExpanded }) {
  // ===== Start: Unit search state =====
  // Stores and evaluates the text filter used by the accordion list.
  const unitsSearchQuery = ref('');
  const normalizedUnitsSearch = computed(() => normalizeSearchValue(unitsSearchQuery.value));
  const hasUnitsSearch = computed(() => normalizedUnitsSearch.value.length > 0);

  const unitMatchesSearch = (unit) => {
    if (!hasUnitsSearch.value) {
      return true;
    }

    const searchableValues = [
      unit.description,
      unit.rems,
      unit.sirh,
      unit.region?.name,
      unit.municipality?.name,
    ];

    return searchableValues.some((value) => normalizeSearchValue(value).includes(normalizedUnitsSearch.value));
  };
  // ===== End: Unit search state =====

  // ===== Start: Unit identity tracking =====
  // Produces a stable signature to reset accordion state when assigned units change.
  const unitKeys = computed(() => props.operationalSecurityCrisis.units
    .map((unit) => unit.id_unit)
    .sort()
    .join('|'));
  // ===== End: Unit identity tracking =====

  // ===== Start: State and municipality grouping =====
  // Groups filtered facilities into the hierarchy rendered by the accordion list.
  const groupedRows = computed(() => {
    const states = [];
    const stateMap = new Map();

    props.operationalSecurityCrisis.units.forEach((item) => {
      if (!unitMatchesSearch(item)) {
        return;
      }

      const stateName = item.region?.name || 'Sin Estado';
      const stateId = item.region?.id || stateName;
      const stateKey = `state:${stateId}`;
      const municipalityName = item.municipality?.name || 'Sin Municipio';
      const municipalityId = item.municipality?.id || municipalityName;
      const municipalityKey = `${stateKey}:municipality:${municipalityId}`;

      if (!stateMap.has(stateKey)) {
        const state = {
          id: item.region?.id || null,
          key: stateKey,
          name: stateName,
          stateKey,
          totalBranches: 0,
          municipalities: [],
          municipalityMap: new Map(),
        };

        stateMap.set(stateKey, state);
        states.push(state);
      }

      const state = stateMap.get(stateKey);

      if (!state.municipalityMap.has(municipalityKey)) {
        const municipality = {
          id: item.municipality?.id || null,
          key: municipalityKey,
          municipalityKey,
          name: municipalityName,
          branches: [],
        };

        state.municipalityMap.set(municipalityKey, municipality);
        state.municipalities.push(municipality);
      }

      const municipality = state.municipalityMap.get(municipalityKey);

      if (!municipality.branches.some((branch) => getBranchKey(branch) === getBranchKey(item))) {
        municipality.branches.push(item);
        state.totalBranches += 1;
      }
    });

    return states.map((state) => ({
      ...state,
      municipalities: state.municipalities.map((municipality) => ({
        ...municipality,
        totalBranches: municipality.branches.length,
      })),
    }));
  });
  // ===== End: State and municipality grouping =====

  // ===== Start: Virtual scroller row projection =====
  // Flattens the expanded hierarchy into rows consumed by vue-virtual-scroller.
  const visibleRows = computed(() => {
    const rows = [];

    groupedRows.value.forEach((state) => {
      rows.push({
        type: 'state',
        key: state.key,
        id: state.id,
        name: state.name,
        stateKey: state.stateKey,
        totalBranches: state.totalBranches,
      });

      if (!isStateExpanded(state.stateKey)) {
        return;
      }

      state.municipalities.forEach((municipality) => {
        rows.push({
          type: 'municipality',
          key: municipality.key,
          id: municipality.id,
          name: municipality.name,
          municipalityKey: municipality.municipalityKey,
          totalBranches: municipality.totalBranches,
          data: municipality,
        });

        if (!isMunicipalityExpanded(municipality.municipalityKey)) {
          return;
        }

        municipality.branches.forEach((branch) => {
          rows.push({
            type: 'facility',
            key: `facility:${getBranchKey(branch)}`,
            branch,
          });
        });
      });
    });

    return rows;
  });
  // ===== End: Virtual scroller row projection =====

  return {
    hasUnitsSearch,
    unitKeys,
    unitsSearchQuery,
    visibleRows,
  };
}
